import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './Gallery.css'; // YOUR Gallery.css FILE
import Navbar from '../Navbar/Navbar'; // Assuming this is your existing Navbar
import Footer from '../Footer/Footer'; // Assuming this is your existing Footer
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronLeft, faChevronRight, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

const Gallery = () => {
    const [allImages, setAllImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('All');

    const [imagesToDisplay, setImagesToDisplay] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [imagesPerPage, setImagesPerPage] = useState(12);

    const [searchTerm, setSearchTerm] = useState(''); // Retained for potential future use, but not in current filter UI

    const [modalImageIndex, setModalImageIndex] = useState(null);

   
       const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.hitkarihospital.com:5005'; // Your backend API URL
  

    const fetchImages = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}/gallery-images`);
            const fetchedImages = response.data;

            // Ensure each image has a category; default to 'Uncategorized' if missing
            const imagesWithCategory = fetchedImages.map(img => ({
                ...img,
                category: img.heading || 'Uncategorized'
            }));

            setAllImages(imagesWithCategory);

            const uniqueCategories = ['All', ...new Set(imagesWithCategory.map(img => img.category))].sort((a, b) => {
                if (a === 'All') return -1;
                if (b === 'All') return 1;
                return a.localeCompare(b);
            });
            setCategories(uniqueCategories);

        } catch (err) {
            setError('Failed to fetch images. Please try again later.');
            console.error("Fetch error:", err);
            setAllImages([]);
            setCategories(['All']);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL]);

    useEffect(() => {
        fetchImages();
    }, [fetchImages]);

    useEffect(() => {
        let filtered = allImages;

        if (selectedCategory !== 'All') {
            filtered = filtered.filter(image => image.category === selectedCategory);
        }

        // Note: Search term filter is removed from this effect as per UI focusing on category
        // If you re-add search input, integrate its filtering here.
        // if (searchTerm) {
        //     filtered = filtered.filter(image =>
        //         (image.filename || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        //         (image.category || '').toLowerCase().includes(searchTerm.toLowerCase())
        //     );
        // }

        const indexOfLastImage = currentPage * imagesPerPage;
        const indexOfFirstImage = indexOfLastImage - imagesPerPage;
        setImagesToDisplay(filtered.slice(indexOfFirstImage, indexOfLastImage));

        if (Math.ceil(filtered.length / imagesPerPage) < currentPage && filtered.length > 0) {
            setCurrentPage(1);
        } else if (filtered.length === 0 && currentPage !== 1) {
            setCurrentPage(1);
        }

    }, [allImages, selectedCategory, currentPage, imagesPerPage /*, searchTerm */]);


    const totalPages = useMemo(() => {
        let source = allImages;
        if (selectedCategory !== 'All') {
            source = source.filter(image => image.category === selectedCategory);
        }
        // if (searchTerm) { ... } // Add if search is re-integrated
        return Math.ceil(source.length / imagesPerPage) || 1; // Ensure at least 1 page
    }, [allImages, selectedCategory, /* searchTerm, */ imagesPerPage]);


    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        setCurrentPage(1);
    };

    const handleImagesPerPageChange = (e) => {
        setImagesPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const openModal = (indexInDisplayed) => {
        // Find the actual index in the currently filtered (but not paginated) list
        let filteredForCategory = allImages;
        if (selectedCategory !== 'All') {
            filteredForCategory = allImages.filter(image => image.category === selectedCategory);
        }
        // The `indexInDisplayed` is relative to the `imagesToDisplay` array.
        // We need to find the corresponding image in `filteredForCategory` to have a stable index for modal navigation.
        // However, for simplicity with current modal logic, we'll use index relative to `imagesToDisplay`.
        // For more robust next/prev across all filtered images, a different approach for modal indexing would be needed.
        setModalImageIndex(indexInDisplayed);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = useCallback(() => {
        setModalImageIndex(null);
        document.body.style.overflow = 'auto';
    }, []);

    const showNextImage = useCallback(() => {
        if (modalImageIndex === null) return;
        setModalImageIndex((prevIndex) => (prevIndex + 1) % imagesToDisplay.length);
    }, [modalImageIndex, imagesToDisplay.length]);

    const showPrevImage = useCallback(() => {
        if (modalImageIndex === null) return;
        setModalImageIndex((prevIndex) => (prevIndex - 1 + imagesToDisplay.length) % imagesToDisplay.length);
    }, [modalImageIndex, imagesToDisplay.length]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (modalImageIndex !== null) {
                if (event.key === 'Escape') closeModal();
                if (event.key === 'ArrowRight') showNextImage();
                if (event.key === 'ArrowLeft') showPrevImage();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [modalImageIndex, closeModal, showNextImage, showPrevImage]);

    // Helper to generate pagination numbers with ellipsis
    const getPageNumbers = () => {
        const pageNeighbours = 1; // Number of pages to show on each side of current page
        const totalNumbers = (pageNeighbours * 2) + 3; // total page numbers to show (current + neighbours + first + last + 2 ellipses)
        const totalBlocks = totalNumbers + 2; // including ellipses

        if (totalPages > totalBlocks) {
            const pages = [];
            const leftBound = currentPage - pageNeighbours;
            const rightBound = currentPage + pageNeighbours;
            const beforeLastPage = totalPages - 1;

            let startPage = Math.max(1, leftBound);
            let endPage = Math.min(totalPages, rightBound);

            if (startPage > 2) {
                pages.push(1);
                pages.push('...');
            } else if (startPage === 2) {
                 pages.push(1);
            }


            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (endPage < beforeLastPage -1 ) {
                 pages.push('...');
                 pages.push(totalPages);
            } else if (endPage < totalPages){
                 pages.push(totalPages);
            }


            return pages;
        }
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    };


    if (loading) {
        return (
            <>
                <Navbar />
                <div className="gallery-loader-container">
                    <div className="gallery-spinner"></div>
                    <p>Loading Gallery...</p>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Navbar />
                <div className="gallery-error-container">
                    <FontAwesomeIcon icon={faTimes} size="3x" style={{ marginBottom: '1rem' }} />
                    <p>{error}</p>
                    <button onClick={fetchImages} className="gallery-button-retry">Try Again</button>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <div className="main-wrapper gallery-page">
                <header className="header">
                    <Navbar />
                </header>
                <div className="breadcrumb-bar">
                    <div className="container-fluid">
                        <div className="row align-items-center">
                            <div className="col-md-12 col-12">
                                <nav aria-label="breadcrumb" className="page-breadcrumb">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item"><a href="/">Home</a></li>
                                        <li className="breadcrumb-item active" aria-current="page">Gallery</li>
                                    </ol>
                                </nav>
                                <h2 className="breadcrumb-title">Our Gallery</h2>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="gallery-container container-fluid mt-4 mb-5">
                    <div className="gallery-controls-wrapper">
                        <div className="gallery-category-filter">
                            <FontAwesomeIcon icon={faFolderOpen} className="filter-icon" />
                            {categories.map(category => (
                                <button
                                    key={category}
                                    className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                                    onClick={() => handleCategoryChange(category)}
                                    title={`View ${category} images`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                        <div className="gallery-items-per-page">
                            <label htmlFor="itemsPerPageSelect">Show:</label>
                            <select
                                id="itemsPerPageSelect"
                                value={imagesPerPage}
                                onChange={handleImagesPerPageChange}
                                className="per-page-select"
                            >
                                <option value="8">8</option>
                                <option value="12">12</option>
                                <option value="20">20</option>
                                <option value="32">32</option>
                                <option value="48">48</option>
                            </select>
                        </div>
                    </div>

                    {selectedCategory !== 'All' && imagesToDisplay.length > 0 && (
                        <h3 className="current-category-header">
                            {selectedCategory}
                        </h3>
                    )}

                    {imagesToDisplay.length > 0 ? (
                        <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 500: 2, 750: 3, 900: 4, 1200: 4 }}>
                            <Masonry gutter="15px">
                                {imagesToDisplay.map((image, index) => (
                                    <div className="gallery-item-stunning" key={image._id || index} onClick={() => openModal(index)}>
                                        <img
                                            src={`${API_BASE_URL}${image.path}`}
                                            alt={image.filename || `Gallery image in ${image.category}`}
                                            className="gallery-img-stunning"
                                            loading="lazy" // Add lazy loading
                                        />
                                        <div className="gallery-item-overlay">
                                            <p>{image.filename || image.category}</p>
                                        </div>
                                    </div>
                                ))}
                            </Masonry>
                        </ResponsiveMasonry>
                    ) : (
                        <div className="gallery-no-results">
                            <h3>No Images Found</h3>
                            <p>
                                {selectedCategory !== 'All' ?
                                 `There are no images in the "${selectedCategory}" category.` :
                                 "There are no images in the gallery yet."}
                            </p>
                            {selectedCategory !== 'All' && (
                                <button onClick={() => handleCategoryChange('All')} className="gallery-button-secondary">
                                    Show All Images
                                </button>
                            )}
                        </div>
                    )}

                    {totalPages > 1 && imagesToDisplay.length > 0 && (
                        <nav className="gallery-pagination" aria-label="Gallery pagination">
                            <ul>
                                <li>
                                    <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} aria-label="Previous page">
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                </li>
                                {getPageNumbers().map((page, index) => (
                                    page === '...' ? (
                                        <li key={`ellipsis-${index}`} className="ellipsis"><span>...</span></li>
                                    ) : (
                                        <li key={page} className={currentPage === page ? 'active' : ''}>
                                            <button onClick={() => paginate(page)}>{page}</button>
                                        </li>
                                    )
                                ))}
                                <li>
                                    <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Next page">
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}
                </div>

                {modalImageIndex !== null && imagesToDisplay[modalImageIndex] && (
                    <div className="gallery-modal-stunning active" onClick={closeModal} role="dialog" aria-modal="true" aria-labelledby="modalTitle">
                        <div className="modal-content-stunning" onClick={(e) => e.stopPropagation()}>
                            <button className="modal-close-btn" onClick={closeModal} aria-label="Close image viewer">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                            {imagesToDisplay.length > 1 && (
                                <>
                                    <button className="modal-nav-btn prev" onClick={showPrevImage} aria-label="Previous image">
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                    <button className="modal-nav-btn next" onClick={showNextImage} aria-label="Next image">
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </>
                            )}
                            <figure className="modal-figure">
                                <img
                                    src={`${API_BASE_URL}${imagesToDisplay[modalImageIndex].path}`}
                                    alt={imagesToDisplay[modalImageIndex].filename || `Image from ${imagesToDisplay[modalImageIndex].category}`}
                                    className="modal-image-stunning"
                                />
                                <figcaption className="modal-caption" id="modalTitle">
                                    <p>{imagesToDisplay[modalImageIndex].filename || imagesToDisplay[modalImageIndex].category}</p>
                                </figcaption>
                            </figure>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
};

export default Gallery;